'use client';

import type { ImageVariant, OptimizedImage } from '@order-up/models';
import React from 'react';

export interface ResponsiveImageProps {
    src: string | OptimizedImage;
    alt: string;
    className?: string;
    style?: React.CSSProperties;
    loading?: 'lazy' | 'eager';
    priority?: boolean; // For hero images (affects loading strategy)
    maxWidth?: number; // Viewport width hint for variant selection
    onLoad?: () => void;
    onError?: () => void;
    fallbackSrc?: string;
    /** Enable blur-up placeholder effect (uses placeholder data from OptimizedImage) */
    enableBlur?: boolean;
    /** Custom placeholder color (fallback if no placeholder data) */
    placeholderColor?: string;
}

/**
 * Smart responsive image component that automatically selects the best variant
 * based on viewport size, device capabilities, and connection speed.
 *
 * Features:
 * - Automatic variant selection based on viewport
 * - WebP format when supported
 * - Blur-up placeholder effect for progressive loading
 * - Dominant color fallback for instant placeholder
 */
export const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
    src,
    alt,
    className = '',
    style = {},
    loading = 'lazy',
    priority = false,
    maxWidth,
    onLoad,
    onError,
    fallbackSrc = '/hero-food-default.jpg',
    enableBlur = true,
    placeholderColor
}) => {
    const [imageError, setImageError] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(true);

    const handleLoad = () => {
        setIsLoading(false);
        onLoad?.();
    };

    const handleError = () => {
        setImageError(true);
        setIsLoading(false);
        onError?.();
    };

    // Parse OptimizedImage from JSON string if needed
    const parseOptimizedImage = (imageSource: string | OptimizedImage): OptimizedImage | null => {
        if (typeof imageSource === 'object') {
            return imageSource;
        }

        // Try to parse as JSON (OptimizedImage)
        if (typeof imageSource === 'string' && imageSource.startsWith('{')) {
            try {
                const parsed = JSON.parse(imageSource);
                if (parsed.original && parsed.variants) {
                    return parsed as OptimizedImage;
                }
            } catch (error) {
                console.debug('Failed to parse OptimizedImage JSON:', error);
            }
        }

        return null;
    };

    const optimizedImage = parseOptimizedImage(src);

    // Get placeholder data for blur effect
    const getPlaceholderStyle = (): React.CSSProperties => {
        if (!enableBlur || !optimizedImage) return {};

        // Priority: blurDataUrl > dominantColor > custom placeholderColor
        if (optimizedImage.placeholder?.blurDataUrl) {
            return {
                backgroundImage: `url(${optimizedImage.placeholder.blurDataUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'blur(20px)',
                transform: 'scale(1.1)', // Slightly larger to avoid blur edge artifacts
            };
        }

        const bgColor = optimizedImage.placeholder?.dominantColor || placeholderColor;
        if (bgColor) {
            return {
                backgroundColor: bgColor,
            };
        }

        return {};
    };

    // If there's an error, use fallback
    if (imageError) {
        return (
            <img
                src={fallbackSrc}
                alt={alt}
                className={className}
                style={style}
                loading={loading}
                onLoad={handleLoad}
                onError={() => console.warn('Fallback image failed to load')}
            />
        );
    }

    // If it's not an OptimizedImage, use regular img
    if (!optimizedImage) {
        const imageSrc = typeof src === 'string' ? src : fallbackSrc;
        return (
            <img
                src={imageSrc}
                alt={alt}
                className={className}
                style={style}
                loading={loading}
                onLoad={handleLoad}
                onError={handleError}
            />
        );
    }

    // Smart variant selection based on viewport and device capabilities
    const getResponsiveSrc = (): string => {
        const viewportWidth = maxWidth || (typeof window !== 'undefined' ? window.innerWidth : 1200);
        const devicePixelRatio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
        const effectiveWidth = viewportWidth * devicePixelRatio;

        // Check if browser supports WebP
        const supportsWebP = typeof window !== 'undefined' &&
            window.HTMLCanvasElement &&
            window.HTMLCanvasElement.prototype.toDataURL &&
            window.HTMLCanvasElement.prototype.toDataURL.call(document.createElement('canvas'), 'image/webp').startsWith('data:image/webp');

        // Select appropriate size category
        let targetVariants: ImageVariant[];
        if (effectiveWidth <= 600) {
            targetVariants = optimizedImage.variants.small;
        } else if (effectiveWidth <= 1200) {
            targetVariants = optimizedImage.variants.medium;
        } else {
            targetVariants = optimizedImage.variants.large;
        }

        // Find best variant (prefer WebP if supported, closest size match)
        let bestVariant: ImageVariant | null = null;

        if (supportsWebP) {
            // Look for WebP variant closest to effective width
            const webpVariants = targetVariants.filter(v => v.format === 'webp');
            bestVariant = webpVariants.find(v => v.width >= effectiveWidth) ||
                webpVariants[webpVariants.length - 1];
        }

        if (!bestVariant) {
            // Fallback to JPEG/PNG variants
            const fallbackVariants = targetVariants.filter(v => v.format !== 'webp');
            bestVariant = fallbackVariants.find(v => v.width >= effectiveWidth) ||
                fallbackVariants[fallbackVariants.length - 1];
        }

        // Final fallback to original
        return bestVariant?.url || optimizedImage.original.url;
    };

    const generateSrcSet = (): string => {
        const srcSetEntries: string[] = [];

        // Add WebP variants
        ['small', 'medium', 'large'].forEach(category => {
            const variants = optimizedImage.variants[category as keyof typeof optimizedImage.variants];
            variants.filter(v => v.format === 'webp').forEach(variant => {
                srcSetEntries.push(`${variant.url} ${variant.width}w`);
            });
        });

        // Add fallback formats
        ['small', 'medium', 'large'].forEach(category => {
            const variants = optimizedImage.variants[category as keyof typeof optimizedImage.variants];
            variants.filter(v => v.format !== 'webp').forEach(variant => {
                srcSetEntries.push(`${variant.url} ${variant.width}w`);
            });
        });

        // Add original as largest option
        srcSetEntries.push(`${optimizedImage.original.url} ${optimizedImage.original.width}w`);

        return srcSetEntries.join(', ');
    };

    const getSizes = (): string => {
        // Responsive sizes based on common breakpoints
        return [
            '(max-width: 640px) 100vw',
            '(max-width: 1024px) 100vw',
            '(max-width: 1280px) 100vw',
            '100vw'
        ].join(', ');
    };

    const placeholderStyles = getPlaceholderStyle();
    const hasBlurPlaceholder = enableBlur && (
        optimizedImage.placeholder?.blurDataUrl ||
        optimizedImage.placeholder?.dominantColor ||
        placeholderColor
    );

    return (
        <div
            className="relative overflow-hidden"
            style={{
                display: 'inline-block',
                width: style?.width,
                height: style?.height,
            }}
        >
            {/* Blur placeholder background */}
            {isLoading && hasBlurPlaceholder && (
                <div
                    className={`absolute inset-0 ${className}`}
                    style={{
                        ...style,
                        ...placeholderStyles,
                        position: 'absolute',
                        zIndex: 1,
                    }}
                    aria-hidden="true"
                />
            )}

            {/* Loading state fallback (when no blur placeholder) */}
            {isLoading && !hasBlurPlaceholder && (
                <div
                    className={`absolute inset-0 bg-gray-200 motion-safe:animate-pulse ${className}`}
                    style={{
                        ...style,
                        position: 'absolute',
                        zIndex: 1,
                    }}
                    aria-label="Loading image..."
                />
            )}

            {/* Optimized responsive image */}
            <img
                src={getResponsiveSrc()}
                srcSet={generateSrcSet()}
                sizes={getSizes()}
                alt={alt}
                className={`${className} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
                style={{
                    ...style,
                    position: 'relative',
                    zIndex: 2,
                }}
                loading={priority ? 'eager' : loading}
                onLoad={handleLoad}
                onError={handleError}
                // Performance hints for critical images
                {...(priority && {
                    fetchPriority: 'high' as any,
                    decoding: 'sync' as any
                })}
            />
        </div>
    );
};

/**
 * Hook for getting optimal image URL based on context
 */
export const useOptimalImageUrl = (
    src: string | OptimizedImage | undefined,
    maxWidth?: number
): string => {
    return React.useMemo(() => {
        if (!src) return '/hero-food-default.jpg';

        // Parse OptimizedImage from JSON if needed
        let optimizedImage: OptimizedImage | null = null;

        if (typeof src === 'object') {
            optimizedImage = src;
        } else if (typeof src === 'string' && src.startsWith('{')) {
            try {
                const parsed = JSON.parse(src);
                if (parsed.original && parsed.variants) {
                    optimizedImage = parsed as OptimizedImage;
                }
            } catch (error) {
                console.debug('Failed to parse OptimizedImage JSON:', error);
            }
        }

        if (!optimizedImage) {
            return typeof src === 'string' ? src : '/hero-food-default.jpg';
        }

        const viewportWidth = maxWidth || 1200;
        const devicePixelRatio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
        const effectiveWidth = viewportWidth * devicePixelRatio;

        // Select appropriate variants
        let targetVariants: ImageVariant[];
        if (effectiveWidth <= 600) {
            targetVariants = optimizedImage.variants.small;
        } else if (effectiveWidth <= 1200) {
            targetVariants = optimizedImage.variants.medium;
        } else {
            targetVariants = optimizedImage.variants.large;
        }

        // Prefer WebP, find best size match
        const webpVariant = targetVariants
            .filter(v => v.format === 'webp')
            .find(v => v.width >= effectiveWidth) ||
            targetVariants.filter(v => v.format === 'webp')[0];

        if (webpVariant) return webpVariant.url;

        // Fallback to other formats
        const fallbackVariant = targetVariants
            .filter(v => v.format !== 'webp')
            .find(v => v.width >= effectiveWidth) ||
            targetVariants.filter(v => v.format !== 'webp')[0];

        return fallbackVariant?.url || optimizedImage.original.url;
    }, [src, maxWidth]);
};

export default ResponsiveImage;


